
import { computed, ref, watchEffect, onActivated, onDeactivated } from "vue";
import { useStore } from "vuex";
import { useCssVar, useTimestamp, useElementSize } from "@vueuse/core";
import _ from "lodash";
import moment from "moment";
import { getTextDirection } from "@/helpers/string";

export default {
  setup() {
    const store = useStore();

    const timestamp = useTimestamp({ interval: 10 });

    const subtitles = computed(() => store.state.session.subtitles),
      fullText = computed(() =>
        _.join(
          _.map(subtitles.value, s => s?.[0] ?? ""),
          ""
        )
      ),
      displayedText = computed(() =>
        _.join(
          _.transform(subtitles.value, (result: string[], s, i: number) => {
            const transition = 50;

            const sliced = (s?.[0] ?? "").slice(
              0,
              _.max([
                0,
                _.floor(
                  (timestamp.value -
                    _.max([
                      i
                        ? subtitles.value[0]?.[1] +
                          _.reduce(result, (sum, v) => sum + v.length, 0) *
                            transition
                        : 0,
                      s?.[1]
                    ])!) /
                    transition
                )
              ])
            );
            result.push(sliced);
            return sliced.length === (s?.[0] ?? "").length;
          }),
          ""
        )
      );

    // Track heights for smooth Y-scrolling
    const textChunksRef = ref<HTMLElement | null>(null),
      visibleFrameRef = ref<HTMLElement | null>(null),
      { height: textChunksHeight } = useElementSize(textChunksRef),
      { height: visibleFrameHeight } = useElementSize(visibleFrameRef),
      textLinesVisibleCssVal = useCssVar("--subtitles-text-lines-visible"),
      emptyLinesVisibleCssVal = useCssVar("--subtitles-empty-lines-visible"),
      textChunksTop = computed(() =>
        subtitles.value?.length < 1
          ? 0
          : _.min([
              0,
              (visibleFrameHeight.value ?? 0) *
                (1 -
                  parseInt(emptyLinesVisibleCssVal.value || "2", 10) /
                    (parseInt(emptyLinesVisibleCssVal.value || "2", 10) +
                      parseInt(textLinesVisibleCssVal.value || "3", 10))) -
                (textChunksHeight.value ?? 0)
            ])
      );

    // Auto-clear subtitles after 5 seconds of silence
    watchEffect(() => {
      const expiresAt = +moment(timestamp.value).subtract(5, "s");

      if (
        subtitles.value?.length &&
        !_.findLast(subtitles.value, s => s?.[1] > expiresAt) &&
        fullText.value === displayedText.value
      ) {
        store.commit("session/UNSET_SUBTITLES");
      }
    });

    const fontsizeCssVal = useCssVar("--subtitles-fontsize"),
      fontsizeCssDefaultVal = fontsizeCssVal.value;

    const fontSizeValue = ref<number>(),
      fontSizeUnit = ref<string>();

    const syncFontSize = () => {
      const match = fontsizeCssVal.value?.match(/^([\d.]+)([a-zA-Z%]+)$/);

      if (match) {
        fontSizeValue.value = +match[1];
        fontSizeUnit.value = match[2];
        return;
      }
    };

    onActivated(() => syncFontSize());
    onDeactivated(() => (fontsizeCssVal.value = fontsizeCssDefaultVal));

    watchEffect(() => {
      if (fontSizeValue.value && fontSizeUnit.value) {
        fontsizeCssVal.value = `${fontSizeValue.value}${fontSizeUnit.value}`;
      }
    });

    const minFontsize = computed(() => (fontSizeUnit.value === "px" ? 8 : 0.3)),
      maxFontSize = computed(() => (fontSizeUnit.value === "px" ? 50 : 3));

    const changeSize = (direction: "up" | "down") => {
      const step = fontSizeUnit.value === "px" ? 2 : 0.1;

      if (direction === "up") {
        fontSizeValue.value = _.min([
          (fontSizeValue.value ?? 0) + step,
          maxFontSize.value
        ]);
      } else if (direction === "down") {
        fontSizeValue.value = _.max([
          (fontSizeValue.value ?? 0) - step,
          minFontsize.value
        ]);
      }
    };

    return {
      displayedText,
      fontSizeValue,
      fontSizeUnit,
      minFontsize,
      maxFontSize,
      changeSize,
      getTextDirection,
      textChunksRef,
      visibleFrameRef,
      textChunksTop,
      ..._.pick(_, ["split", "map", "join"])
    };
  }
};
